// https://vuex.vuejs.org/guide/modules.html

import { GET_PROJECT } from '../../schema/businessUnit';
import store from '../index';

export default {
    namespaced: true,

    state: {
        project_id: undefined,
        project: undefined,
        // Track loading errors for degraded mode
        loadingErrors: [],
    },

    getters: {
        //Selected (current) project of the selected Business Unit
        project_id: (state) => {
            return state.project_id;
        },
        project: (state) => {
            return state.project;
        },

        loading: (state) => {
            return (
                state.project === undefined && state.project_id !== undefined
            );
        },

        // Returns true if the project loaded with some errors (degraded mode)
        hasDegradedMode: (state) => {
            return state.loadingErrors.length > 0;
        },

        // Returns the list of services that failed to load
        loadingErrors: (state) => {
            return state.loadingErrors;
        },
    },

    mutations: {
        clear: (state) => {
            state.project = undefined;
            state.loadingErrors = [];
        },
        set_project_id: (state, project_id) => {
            state.project_id = project_id;
        },
        update_project: (state, project) => {
            state.project = project;
        },
        add_loading_error: (state, error) => {
            state.loadingErrors.push(error);
        },
        clear_loading_errors: (state) => {
            state.loadingErrors = [];
        },
    },

    actions: {
        wait_project_load: async ({ getters }) => {
            while (getters.loading) {
                await new Promise((r) => setTimeout(r, 100));
            }
        },

        /**
         * Helper to dispatch actions with error resilience.
         * Logs errors and tracks them for degraded mode instead of failing.
         */
        _safeDispatch: async ({ commit }, { action, payload, serviceName }) => {
            try {
                await store.dispatch(action, payload, { root: true });
            } catch (error) {
                console.error(
                    `[${serviceName}] Failed to load: ${error.message}`,
                );
                commit('add_loading_error', {
                    service: serviceName,
                    message: error.message,
                    timestamp: new Date().toISOString(),
                });
            }
        },

        switch_project: async ({ commit, dispatch, rootGetters }, payload) => {
            commit('clear');
            commit('set_project_id', payload.project_id);

            try {
                // Critical request: if this fails, we cannot continue
                const { data, loading } = await rootGetters.apollo.query({
                    query: GET_PROJECT,
                    variables: {
                        id: payload.project_id,
                    },
                    fetchPolicy: 'no-cache',
                    notifyOnNetworkStatusChange: true,
                });

                if (loading || data === undefined) {
                    throw new Error('Project data not available');
                }

                // Core project data loaded successfully
                commit('update_project', data.project);

                const projectId = data.project.id;

                // Secondary services: load them resiliently (non-blocking)
                // These can fail without preventing the project from being used
                const secondaryLoads = [
                    dispatch('_safeDispatch', {
                        action: 'registries/load_registries',
                        payload: { project_id: projectId },
                        serviceName: 'registries',
                    }),
                    dispatch('_safeDispatch', {
                        action: 'registries/subscribe',
                        payload: { project_id: projectId },
                        serviceName: 'registries/subscribe',
                    }),
                    dispatch('_safeDispatch', {
                        action: 'deviceTypes/load_device_types',
                        payload: { project_id: projectId },
                        serviceName: 'deviceTypes',
                    }),
                    dispatch('_safeDispatch', {
                        action: 'topics/subscribe',
                        payload: {},
                        serviceName: 'topics',
                    }),
                    dispatch('_safeDispatch', {
                        action: 'actionTypes/subscribe',
                        payload: { project_id: projectId },
                        serviceName: 'actionTypes',
                    }),
                ];

                // Optional services based on feature flags
                if (store.getters['connectors/activated'] === true) {
                    secondaryLoads.push(
                        dispatch('_safeDispatch', {
                            action: 'connectors/loadConnectors',
                            payload: { projectId: projectId },
                            serviceName: 'connectors',
                        }),
                    );
                }

                secondaryLoads.push(
                    dispatch('_safeDispatch', {
                        action: 'alertRules/list',
                        payload: {
                            project_id: projectId,
                            filters: {
                                page: 1,
                                item_per_page: 10,
                            },
                        },
                        serviceName: 'alertRules',
                    }),
                    dispatch('_safeDispatch', {
                        action: 'alertRules/load_notifiable',
                        payload: { project_id: projectId },
                        serviceName: 'alertRules/notifiable',
                    }),
                    dispatch('_safeDispatch', {
                        action: 'alerts/subscribe',
                        payload: { project_id: projectId },
                        serviceName: 'alerts',
                    }),
                );

                // Wait for all secondary loads to complete (they won't throw)
                await Promise.all(secondaryLoads);

                return data.project;
            } catch (error) {
                // Only the critical GET_PROJECT failure should reach here
                console.error(
                    `[switch_project] Critical error loading project: ${error.message}`,
                );
                commit('set_project_id', undefined);
                throw error;
            }
        },
    },
};
